import { useEffect, useRef, useState } from 'react';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import { useActiveAccountId } from '@lifeomic/phc-web-toolkit/src/hooks';
import { getPractitionerDisplayName } from '@lifeomic/phc-web-toolkit/src/util/fhir';

const practitionerFhirService = new FhirService('Practitioner');

/**
 * Resolves practitioner IDs to display names via FHIR Practitioner getByIds.
 * Deduplicates requests and avoids repeated network calls when the same IDs
 * are requested (e.g. after state updates).
 *
 * @param practitionerIds - Array of Practitioner resource IDs (e.g. from Reference)
 * @returns Map of practitioner id -> display name (missing/failed lookups are omitted)
 */
export function usePractitionerNames(
  practitionerIds: string[],
): Record<string, string> {
  const accountId = useActiveAccountId();
  const auth = useServiceAuth();
  const [fetchPractitioners] = useService(practitionerFhirService.getByIds);
  const [names, setNames] = useState<Record<string, string>>({});
  const requestedIdsRef = useRef<Set<string>>(new Set());

  const idsKey = practitionerIds.length
    ? practitionerIds.slice().sort().join(',')
    : '';

  useEffect(() => {
    if (!accountId || !auth || practitionerIds.length === 0) {
      return;
    }
    const alreadyRequested = requestedIdsRef.current;
    const missingIds = practitionerIds.filter(id => !alreadyRequested.has(id));
    if (missingIds.length === 0) return;

    missingIds.forEach(id => alreadyRequested.add(id));

    let cancelled = false;
    void (async () => {
      try {
        const response = await fetchPractitioners(accountId, missingIds);
        const entry = response?.data?.entry ?? [];
        const practitioners = entry
          .map((e: { resource?: fhir.Practitioner }) => e.resource)
          .filter(Boolean) as fhir.Practitioner[];
        if (cancelled) return;
        setNames(prev => {
          const next = { ...prev };
          practitioners.forEach(p => {
            if (p.id) {
              next[p.id] = getPractitionerDisplayName(
                p as unknown as fhir3.Practitioner,
                { defaultName: '—' },
              );
            }
          });
          return next;
        });
      } catch {
        missingIds.forEach(id => alreadyRequested.delete(id));
      }
    })();
    return () => {
      cancelled = true;
    };
    // idsKey is the stable dependency; we read practitionerIds inside for the actual list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, auth, idsKey, fetchPractitioners]);

  return names;
}
